import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Eye, Edit2, Ban, FileText, Printer, Download, Mail,
  Copy, ExternalLink
} from 'lucide-react';
import { erpToast } from './ERPToast';
import '../../styles/erp-theme.css';

/**
 * Reusable right-click context menu provider for grids.
 *
 * Usage:
 *   const { onRowContextMenu, ContextMenuPortal } = useERPContextMenu({
 *     idField: 'ClientId',
 *     copyKeys: [{ field: 'ClientId', label: 'Copy ClientId' }, { field: 'LicenseKey', label: 'Copy LicenseKey' }],
 *     onView, onEdit, onVoid, onPreview, onPrint, onExport, onEmail, onOpenInNewTab
 *   });
 *   <tr onContextMenu={(e) => onRowContextMenu(e, row)} />
 *   {ContextMenuPortal}
 */
export function useERPContextMenu(handlers = {}) {
  const {
    idField = 'id',
    copyKeys = [],
    extraItems = [],
    onView, onEdit, onVoid, onPreview, onPrint, onExport, onEmail, onOpenInNewTab
  } = handlers;

  const [state, setState] = useState({ open: false, x: 0, y: 0, row: null });
  const menuRef = useRef(null);

  const close = useCallback(() => setState(s => ({ ...s, open: false, row: null })), []);

  // Close on outside click / Escape / scroll
  useEffect(() => {
    if (!state.open) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) close();
    };
    const onKey   = (e) => { if (e.key === 'Escape') close(); };
    const onScroll = () => close();
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [state.open, close]);

  const onRowContextMenu = useCallback((e, row) => {
    e.preventDefault();
    const menuWidth = 220;
    const menuHeight = 320;
    const x = Math.min(e.clientX, window.innerWidth  - menuWidth - 8);
    const y = Math.min(e.clientY, window.innerHeight - menuHeight - 8);
    setState({ open: true, x: Math.max(8, x), y: Math.max(8, y), row });
  }, []);

  const handle = (fn) => (e) => {
    e.stopPropagation();
    close();
    fn && fn(state.row);
  };

  const copy = (text, label) => () => {
    if (text == null || text === '') {
      erpToast.warning(`${label} is empty`);
      close();
      return;
    }
    try {
      navigator.clipboard.writeText(String(text));
      erpToast.success(`${label} copied`);
    } catch {
      erpToast.error(`Failed to copy ${label}`);
    }
    close();
  };

  const row = state.row || {};
  const rowLabel = row[idField] ? String(row[idField]) : 'Row';

  const items = [
    onView    && { key: 'view',    label: 'View',    Icon: Eye,      cls: 'act-view',    onClick: handle(onView) },
    onEdit    && { key: 'edit',    label: 'Edit',    Icon: Edit2,    cls: 'act-edit',    onClick: handle(onEdit) },
    onPreview && { key: 'preview', label: 'Preview', Icon: FileText, cls: 'act-preview', onClick: handle(onPreview) },
    onPrint   && { key: 'print',   label: 'Print',   Icon: Printer,  cls: 'act-print',   onClick: handle(onPrint) },
    onExport  && { key: 'export',  label: 'Export',  Icon: Download, cls: 'act-export',  onClick: handle(onExport) },
    onEmail   && { key: 'email',   label: 'Email',   Icon: Mail,     cls: 'act-email',   onClick: handle(onEmail) },
  ].filter(Boolean);

  const ContextMenuPortal = state.open && typeof document !== 'undefined'
    ? createPortal(
        <div ref={menuRef} className="erp-context-menu" style={{ top: state.y, left: state.x }} data-testid="erp-context-menu">
          <div className="erp-context-header">{rowLabel}</div>
          {items.map(it => {
            const Icon = it.Icon;
            return (
              <button key={it.key} className={`erp-action-item ${it.cls}`} onClick={it.onClick} data-testid={`ctx-${it.key}`}>
                <Icon size={13} /> <span>{it.label}</span>
              </button>
            );
          })}
          {copyKeys.length > 0 && <div className="erp-action-divider" />}
          {copyKeys.map(ck => (
            <button key={ck.field} className="erp-action-item act-copy" onClick={copy(row[ck.field], ck.label || `Copy ${ck.field}`)} data-testid={`ctx-copy-${ck.field}`}>
              <Copy size={13} /> <span>{ck.label || `Copy ${ck.field}`}</span>
            </button>
          ))}
          {onOpenInNewTab && (<>
            <div className="erp-action-divider" />
            <button className="erp-action-item act-open" onClick={handle(onOpenInNewTab)} data-testid="ctx-open">
              <ExternalLink size={13} /> <span>Open in new tab</span>
            </button>
          </>)}
          {onVoid && (<>
            <div className="erp-action-divider" />
            <button className="erp-action-item act-void danger" onClick={handle(onVoid)} data-testid="ctx-void">
              <Ban size={13} /> <span>Void</span>
            </button>
          </>)}
          {extraItems.length > 0 && <div className="erp-action-divider" />}
          {extraItems.map((it, idx) => (
            <button
              key={it.testId || it.label || idx}
              className={`erp-action-item ${it.danger ? 'act-void danger' : 'act-view'}`}
              onClick={handle(it.onClick)}
              data-testid={it.testId || `ctx-extra-${idx}`}
            >
              <span>{it.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )
    : null;

  return { onRowContextMenu, ContextMenuPortal, closeContextMenu: close };
}

export default useERPContextMenu;
